import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FlatList,
  Text,
  TouchableOpacity,
  View,
  ActivityIndicator
} from 'react-native'
import styled from 'styled-components/native'
import { H4 } from '../../../components/common/Text'
import CopyShortUrlButton from './CopyShortUrlButton'
import { Flex } from '../../../components/FlexBox'
import Button from '../../../components/common/Button'
import truncate from '../../../utils/truncate'
import { ShortenedUrl } from '../hooks/useUrlShortener'

enum LoadMoreState {
  CanLoad = 'can-load',
  Loading = 'loading',
  LoadMore = 'load-more'
}

const PAGES_BEFORE_LOAD_MORE = 10

const openUrl = (url: string) => window.open(url, '_blank')

const UrlText = styled(Text)`
  ${({ theme: { colors } }) => `
    color: ${colors.brightPrimary};
    font-size: 16px;
  `}
`

const Container = styled(View)`
  ${({ theme: { space } }) => `
    flex: 1;
    margin: ${space[3]}px;
  `}
`

export const UrlLink = ({
  url,
  numberOfLines = 1,
  ellipsizeMode
}: {
  url: string
  numberOfLines?: number
  ellipsizeMode?: string
}) => (
  <TouchableOpacity onPress={() => openUrl(url)}>
    <UrlText numberOfLines={numberOfLines} ellipsizeMode={ellipsizeMode}>
      {url}
    </UrlText>
  </TouchableOpacity>
)

const RowContainer = styled(View)`
  ${({ theme: { space }, dark }) => `
    flex-direction: row;
    align-items: center;
    background-color: ${
      dark ? `rgba(196, 196, 196, 0.14)` : `rgba(0, 104, 144, 0.1)`
    };
    padding: ${space[3]}px;
    border-radius: 32px;
    justify-content: space-between;
    margin: ${space[1]}px 0px;
  `}
`

const HeaderRowContainer = styled(RowContainer)`
  ${({ theme: { space } }) => `
    margin: ${space[2]}px 0px;
  `}
`

const DeleteButton = styled(Button).attrs(({ theme: { colors } }) => ({
  buttonStyle: { backgroundColor: colors.danger }
}))``

const LongUrlCell = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  alignItems: 'center'
}))``

const ShortUrlCell = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end'
}))``

const DeleteButtonContainer = styled(View)`
  ${({ theme: { space } }) => `
    margin-left: ${space[2]}px;
  `}
`

const MAX_CHARACTER_URL_COUNT = 90

interface UrlRowProps {
  shortUrl: ShortenedUrl
  dark: boolean
  onSelect: (id: string) => void
  onDelete: (id: string) => void
}

const UrlRow = ({ shortUrl, dark, onSelect, onDelete }: UrlRowProps) => {
  const { id, sourceUrl = '', targetUrl = '' } = shortUrl

  if (!id) return null // Skip rendering if no ID

  return (
    <RowContainer dark={dark}>
      <LongUrlCell>
        <UrlLink url={truncate(targetUrl, MAX_CHARACTER_URL_COUNT, true)} />
      </LongUrlCell>
      <ShortUrlCell>
        <UrlLink url={sourceUrl} />
        <CopyShortUrlButton url={sourceUrl} />
        <Button onPress={() => onSelect(id)} label="Edit" />
        <DeleteButtonContainer>
          <DeleteButton onPress={() => onDelete(id)} label="Delete" />
        </DeleteButtonContainer>
      </ShortUrlCell>
    </RowContainer>
  )
}

const ListHeader = () => {
  const { t } = useTranslation()
  return (
    <HeaderRowContainer>
      <H4>{t('urlShortener:longUrl')}</H4>
      <H4>{t('urlShortener:shortUrl')}</H4>
    </HeaderRowContainer>
  )
}

const LoadingContainer = styled(View)`
  padding: 16px;
  align-items: center;
  justify-content: center;
`

const LoadMoreButton = styled(Button)`
  margin-top: 8px;
  min-width: 150px;
`

interface ShortUrlsTableProps {
  shortUrls: ShortenedUrl[]
  onSelect: (id: string) => void
  onDelete: (id: string) => void
  onLoadMore: () => void
  isLoading: boolean
  hasMore: boolean
}

const ShortUrlsTable = ({
  shortUrls,
  onSelect,
  onDelete,
  onLoadMore,
  isLoading,
  hasMore
}: ShortUrlsTableProps) => {
  const { t } = useTranslation()
  const [pagesLoaded, setPagesLoaded] = useState(0)
  const [loadMoreState, setLoadMoreState] = useState<LoadMoreState>(
    LoadMoreState.CanLoad
  )

  const handleLoadMore = useCallback(() => {
    if (
      isLoading ||
      loadMoreState === LoadMoreState.Loading ||
      !hasMore ||
      shortUrls.length === 0
    )
      return

    // Only load more if we've scrolled to the bottom
    if (pagesLoaded >= PAGES_BEFORE_LOAD_MORE - 1) {
      setLoadMoreState(LoadMoreState.LoadMore)
      return
    }

    // Only load more if we have more items to load
    if (shortUrls.length > 0) {
      setLoadMoreState(LoadMoreState.Loading)
      onLoadMore()
      setPagesLoaded(prev => prev + 1)

      // Reset to can-load after a short delay to allow state to update
      setTimeout(() => {
        setLoadMoreState(LoadMoreState.CanLoad)
      }, 100)
    }
  }, [
    isLoading,
    pagesLoaded,
    onLoadMore,
    loadMoreState,
    hasMore,
    shortUrls.length
  ])

  const handleLoadMoreClick = useCallback(() => {
    setLoadMoreState(LoadMoreState.Loading)
    setPagesLoaded(0)
    onLoadMore()

    // Reset to can-load after loading more
    setTimeout(() => {
      setLoadMoreState(LoadMoreState.CanLoad)
    }, 100)
  }, [onLoadMore])

  const renderFooter = () => {
    if (loadMoreState === LoadMoreState.LoadMore) {
      return (
        <LoadingContainer>
          <LoadMoreButton
            onPress={handleLoadMoreClick}
            label={t('urlShortener:loadMore')}
            loading={isLoading}
          />
        </LoadingContainer>
      )
    }

    if (!hasMore) {
      return null
    }

    return (
      <LoadingContainer>
        {isLoading && <ActivityIndicator size="small" />}
      </LoadingContainer>
    )
  }

  const renderItem = ({
    item,
    index
  }: {
    item: ShortenedUrl
    index: number
  }) => {
    // Skip items without an ID
    if (!item.id) return null

    // Ensure we have a valid ID before rendering
    const itemId = item.id || ''

    return (
      <UrlRow
        key={`${itemId}-${index}`}
        shortUrl={item}
        dark={index % 2 === 0}
        onSelect={() => onSelect(itemId)}
        onDelete={() => onDelete(itemId)}
      />
    )
  }

  return (
    <Container>
      <FlatList
        data={shortUrls}
        onEndReachedThreshold={0.1}
        renderItem={renderItem}
        keyExtractor={item => item.id || ''}
        ListHeaderComponent={ListHeader}
        ListFooterComponent={renderFooter}
        onEndReached={handleLoadMore}
        removeClippedSubviews={false}
      />
    </Container>
  )
}

export default ShortUrlsTable
